import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { FileText, Plus, Search, X, Trash2, ChevronUp, ChevronDown, DollarSign } from 'lucide-react';
import { COMPANY } from '../../../config/company';

const EstimateLineItemsSection = ({
  language,
  lead,
  lineItems,
  showTemplateSearch,
  setShowTemplateSearch,
  searchQuery,
  setSearchQuery,
  filteredTemplates,
  projectTypeLabels,
  unitTypes,
  handleInputChange,
  handleLineItemChange,
  addLineItem,
  addCategoryCluster,
  removeLineItem,
  moveLineItemUp,
  moveLineItemDown,
  fetchLineItemTemplates,
  formatCurrency
}) => {
  return (
    <Card data-testid="estimate-line-items-section">
      <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center">
            <FileText className="mr-2 h-5 w-5" />
            {language === 'es' ? 'Partidas Detalladas' : 'Itemized Line Items'}
          </span>
          <div className="flex space-x-2">
            <Button 
              size="sm" 
              variant="secondary" 
              onClick={() => { fetchLineItemTemplates(); setShowTemplateSearch(true); }}
              data-testid="line-items-library-btn"
            >
              <Search className="h-4 w-4 mr-1" /> 
              {language === 'es' ? 'Biblioteca' : 'Library'}
            </Button>
            <Button 
              size="sm" 
              variant="secondary" 
              onClick={() => addLineItem()}
              data-testid="add-custom-line-item-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> 
              {language === 'es' ? 'Custom' : 'Custom'}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Itemized vs Non-Itemized Toggle */}
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">
                {language === 'es' ? 'Mostrar Precios en PDF' : 'Show Prices in PDF'}
              </p>
              <p className="text-xs text-amber-600">
                {language === 'es' ? 'Desactivar para mostrar solo el alcance y total' : 'Turn off to show only scope and total'}
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={lead.show_itemized_pricing !== false}
              onChange={(e) => handleInputChange('show_itemized_pricing', e.target.checked)}
              className="sr-only peer"
              data-testid="show-prices-toggle"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
            <span className="ml-2 text-sm font-medium text-amber-800">
              {lead.show_itemized_pricing !== false ? (language === 'es' ? 'Sí' : 'Yes') : 'No'}
            </span>
          </label>
        </div>

        {/* Template Search */}
        {showTemplateSearch && (
          <div className="mb-6 p-4 bg-gray-100 rounded-lg border-2" style={{ borderColor: COMPANY.primaryColor }}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold">{language === 'es' ? 'Buscar en Biblioteca' : 'Search Library'}</h4>
              <Button size="sm" variant="ghost" onClick={() => setShowTemplateSearch(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Input 
              placeholder={language === 'es' ? 'Buscar partidas...' : 'Search items...'} 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              className="mb-4" 
              autoFocus 
              data-testid="template-search-input"
            />
            <div className="max-h-96 overflow-y-auto space-y-1">
              {filteredTemplates.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  {language === 'es' ? 'Sin resultados' : 'No results'}
                </p>
              ) : (
                Object.entries(filteredTemplates.reduce((acc, t) => { 
                  const cat = projectTypeLabels[t.project_type] || t.project_type || 'General'; 
                  if (!acc[cat]) acc[cat] = []; 
                  acc[cat].push(t); 
                  return acc; 
                }, {}))
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([category, templates]) => (
                    <div key={category} className="mb-3">
                      <div 
                        className="px-3 py-1.5 text-sm font-semibold rounded-t sticky top-0 flex justify-between items-center" 
                        style={{ backgroundColor: `${COMPANY.primaryColor}20`, color: COMPANY.primaryColor }}
                      >
                        <span>{category} ({templates.length})</span>
                        <Button 
                          size="sm" 
                          style={{ backgroundColor: COMPANY.primaryColor }} 
                          className="text-white h-6 text-xs px-2" 
                          onClick={() => addCategoryCluster(templates)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {language === 'es' ? 'Todos' : 'All'} ({templates.length})
                        </Button>
                      </div>
                      {templates.map((template) => (
                        <div 
                          key={template.id} 
                          className="p-2 bg-white border-x border-b hover:bg-amber-50 flex justify-between items-center"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-sm">
                              {language === 'es' && template.title_es ? template.title_es : template.title}
                            </div>
                            <div className="text-xs text-gray-500">
                              {template.unit || 'SQ'} • ${template.unit_price || template.default_price || 0}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700 text-white ml-2 h-7 text-xs" 
                            onClick={() => addLineItem(template)}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            {language === 'es' ? 'Agregar' : 'Add'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ))
              )}
            </div>
          </div>
        )}
        
        {lineItems.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            {language === 'es' ? 'Sin partidas aún.' : 'No line items yet.'}
          </p>
        ) : (
          <>
            {/* Header Row */}
            <div className="flex gap-2 mb-2 text-xs font-semibold text-gray-500 border-b pb-2">
              <div className="w-12 text-center shrink-0">#</div>
              <div className="flex-1 min-w-0">{language === 'es' ? 'Descripción' : 'Description'}</div>
              <div className="w-20 text-center shrink-0">{language === 'es' ? 'Unidad' : 'Unit'}</div>
              <div className="w-24 text-center shrink-0">{language === 'es' ? 'Cant.' : 'Qty'}</div>
              <div className="w-28 text-right shrink-0">{language === 'es' ? 'Precio/U' : 'Unit Price'}</div>
              <div className="w-28 text-right shrink-0">Total</div>
              <div className="w-10 shrink-0"></div>
            </div>
            
            {lineItems.map((item, index) => (
              <div 
                key={item.id} 
                className="mb-3 pb-3 border-b last:border-0 group hover:bg-gray-50 rounded-lg p-2 -mx-2"
                data-testid={`line-item-row-${index}`}
              >
                <div className="flex gap-2 items-center">
                  {/* Reorder buttons and item number */}
                  <div className="w-12 flex flex-col items-center shrink-0">
                    <div className="flex flex-col gap-0.5">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className={`h-5 w-5 p-0 ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'}`}
                        onClick={() => moveLineItemUp(index)}
                        disabled={index === 0}
                        title={language === 'es' ? 'Mover arriba' : 'Move up'}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <span className="text-xs font-semibold text-gray-500 text-center">{index + 1}</span>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className={`h-5 w-5 p-0 ${index === lineItems.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'}`}
                        onClick={() => moveLineItemDown(index)}
                        disabled={index === lineItems.length - 1}
                        title={language === 'es' ? 'Mover abajo' : 'Move down'}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Input 
                      placeholder={language === 'es' ? 'Título' : 'Title'} 
                      value={item.title} 
                      onChange={(e) => handleLineItemChange(item.id, 'title', e.target.value)} 
                      className="text-sm" 
                    />
                  </div>
                  <div className="w-20 shrink-0">
                    <Select 
                      value={item.unit || 'SQ'} 
                      onValueChange={(value) => handleLineItemChange(item.id, 'unit', value)}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {unitTypes.map(u => (
                          <SelectItem key={u.value} value={u.value}>{u.value}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24 shrink-0">
                    <Input 
                      type="number" 
                      value={item.quantity ? Math.round(item.quantity * 100) / 100 : ''} 
                      onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)} 
                      className="text-center text-sm" 
                    />
                  </div>
                  <div className="w-28 shrink-0">
                    <Input 
                      type="number" 
                      value={item.unit_price ? Math.round(item.unit_price * 100) / 100 : ''} 
                      onChange={(e) => handleLineItemChange(item.id, 'unit_price', e.target.value)} 
                      className="text-right text-sm" 
                      step="0.01" 
                    />
                  </div>
                  <div className="w-28 text-right font-semibold text-sm text-green-600 shrink-0">
                    {formatCurrency(Math.round((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0) * 100) / 100)}
                  </div>
                  <div className="w-10 text-right shrink-0">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-red-500 h-8 w-8 p-0" 
                      onClick={() => removeLineItem(item.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="ml-14">
                  <Input 
                    placeholder={language === 'es' ? 'Descripción' : 'Description'} 
                    value={item.description || ''} 
                    onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)} 
                    className="mt-2 text-xs text-gray-600" 
                  />
                </div>
              </div>
            ))}
          </>
        )}
        
        {/* Add Line Item Button at bottom */}
        <div className="mt-4 pt-4 border-t flex justify-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => { fetchLineItemTemplates(); setShowTemplateSearch(true); }} 
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
            data-testid="search-library-bottom-btn"
          >
            <Search className="h-4 w-4 mr-2" /> 
            {language === 'es' ? 'Buscar en Biblioteca' : 'Search Library'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => addLineItem()}
            data-testid="add-line-item-bottom-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> 
            {language === 'es' ? 'Agregar Partida' : 'Add Line Item'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EstimateLineItemsSection;
